import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import {
  TableBody,
  TableHead,
  useMediaQuery,
  Tooltip,
  Link
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import InfoIcon from '@mui/icons-material/Info';
import LaunchIcon from '@mui/icons-material/Launch';
import IconButton from '@mui/material/IconButton';
import SortingColumn from '#component/table/sorting-column/sorting-column';
import DeviceIcon from '#component/icon/device-icon';
import { getLabel } from '#component/device/dashboard/helper';
import PreloaderComponent from '#component/preloader';
import NotFoundMessage from '#component/general/not-found-message';
import { toggleDeviceAlert, setSelectedDeviceWithAlert } from '#action/project/devices-popup';
import { setAlertToDevice, getSaveMessage, mergeWithAlerts } from './helper';
import {
  StyledPaper,
  StyledTable,
  StyledHeaderRow,
  StyledTableRow,
  StyledTableCell,
  TableCellType,
  CheckboxCell,
  TitleCellContainer,
  DisabledByText,
  TableCheckbox,
  TableAlertCheckbox,
  UncheckedAlertCheckbox,
  BodyCheckbox,
  StyledChip,
  BottomInfoMessage
} from './styles';

const TableView = ({
  devices,
  loading,
  meta,
  dispatch,
  projectId,
  selected,
  setSelected,
  selectedAlert,
  setSelectedAlert,
  addMode,
  openedFromCreateForm,
  showModeOnly,
  data,
  changeOrderAndOrderBy,
  occupiedDevices,
  alertId,
  showChangesLabel,
  setFormDevices,
  formDevices
}) => {
  const {
    order,
    orderBy,
    newlyAddedDevices,
    stateDiff
  } = useSelector(s => s.projectDevicesPopup, shallowEqual);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const saveMessage = getSaveMessage(data);
  const selectedAlerts = devices.filter(d => d.alertOn);

  const isSelected = (id, selectedData) => selectedData.map(s => s.id).indexOf(id) !== -1;

  const isNewAddedDevice = id => {
    const device = newlyAddedDevices.find(addedDevices => addedDevices.id === id);

    if (device) {
      return stateDiff.find(diffDevice => diffDevice.id === device.id);
    }

    return false;
  };

  const handleClick = (event, device) => {
    if (event.target.getAttribute('data-checkbox')) return;

    const selectedIndex = selected.map(s => s.id).indexOf(device.id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, device);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleAlertClick = (event, device) => {
    const { target } = event;
    const selectedIndex = selectedAlert.map(s => s.id).indexOf(device.id);
    let newSelected = [];

    const deviceWithAlert = setAlertToDevice(device, selectedAlert, target.checked);

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedAlert, deviceWithAlert);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedAlert.slice(1));
    } else if (selectedIndex === selectedAlert.length - 1) {
      newSelected = newSelected.concat(selectedAlert.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedAlert.slice(0, selectedIndex),
        selectedAlert.slice(selectedIndex + 1)
      );
    }

    if (addMode) {
      dispatch(setSelectedDeviceWithAlert(deviceWithAlert));
    } else {
      const newFormData = formDevices.map(d => {
        if (d.id === deviceWithAlert.id) {
          return {
            ...d,
            alertOn: deviceWithAlert.alertOn
          };
        }

        return d;
      });

      setFormDevices?.(newFormData);
    }

    dispatch(toggleDeviceAlert(deviceWithAlert));
    setSelectedAlert(newSelected);

    if (alertId) {
      const updatedWithAlerts = mergeWithAlerts(selected, newSelected);
      setSelected(updatedWithAlerts);
    }
  };

  const handleSelectAllClick = event => {
    if (event.target.checked) {
      setSelected(devices);
      return;
    }
    setSelected([]);
  };

  const handleEnableAllAlerts = event => {
    const { checked } = event.target;
    const allDevices = devices.map(d => ({ ...d, label: d.title, alertOn: checked }));

    if (addMode) {
      dispatch(setSelectedDeviceWithAlert(allDevices));
    } else {
      setFormDevices?.(allDevices);
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    dispatch(
      changeOrderAndOrderBy(
        newOrder,
        property,
        projectId,
        addMode,
        openedFromCreateForm,
        data
      )
    );
  };

  if (!devices?.length && loading) {
    return <PreloaderComponent />;
  }

  if (!devices?.length && !loading) {
    return (
      <NotFoundMessage>
        No devices
      </NotFoundMessage>
    );
  }

  return (
    <StyledPaper>
      <StyledTable
        loading={loading}
        size="small"
      >
        <TableHead>
          <StyledHeaderRow>
            {!showModeOnly &&
            <StyledTableCell padding="checkbox">
              <TableCheckbox
                color="primary"
                checked={(devices.length > 0 && selected.length > 0) &&
                  selected.length === devices.length}
                indeterminate={selected.length > 0 && selected.length !== devices.length}
                onChange={handleSelectAllClick}
                inputProps={{
                  'aria-label': 'select all'
                }}
              />
            </StyledTableCell>}
            {alertId &&
              <StyledTableCell padding="checkbox">
                <TableAlertCheckbox
                  color="primary"
                  checked={(devices.length > 0 && selectedAlerts.length > 0) &&
                    selectedAlerts.length === devices.length}
                  indeterminate={
                    selectedAlerts.length > 0 && selectedAlerts.length !== devices.length
                  }
                  indeterminateIcon={
                    <UncheckedAlertCheckbox>
                      <NotificationsActiveRoundedIcon />
                    </UncheckedAlertCheckbox>
                  }
                  onChange={handleEnableAllAlerts}
                  inputProps={{
                    'aria-label': 'enable all alerts'
                  }}
                  icon={<NotificationsOffOutlinedIcon />}
                  checkedIcon={<NotificationsActiveRoundedIcon />}
                />
              </StyledTableCell>}
            <SortingColumn
              id="title"
              title="Device"
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            {!(isMobile && alertId) &&
              <TableCellType withoutHover={showModeOnly}>
                Type
              </TableCellType>}
          </StyledHeaderRow>
        </TableHead>
        <TableBody>
          {devices?.map(device => {
            const isItemSelected = isSelected(device.id, selected);
            const isAlertSelected = !!device.alertOn;
            const occupiedByDevice = occupiedDevices?.find(ed => ed.id === device.id);
            const isAddedDevice = isNewAddedDevice(device.id);
            return (
              <StyledTableRow
                key={device.id}
                hover={!loading}
                disabled={!!occupiedByDevice}
                withoutHover={showModeOnly}
                onClick={!showModeOnly ? event => handleClick(event, device) : null}
                selected={isItemSelected}
              >
                {!showModeOnly &&
                  <CheckboxCell padding="checkbox">
                    <BodyCheckbox
                      color="secondary"
                      checked={isItemSelected}
                      inputProps={{
                        'aria-labelledby': device.id
                      }}
                    />
                  </CheckboxCell>}
                {alertId &&
                  <CheckboxCell padding="checkbox">
                    <BodyCheckbox
                      color="secondary"
                      checked={isAlertSelected}
                      inputProps={{
                        'aria-labelledby': `${device.id}-alert${isAlertSelected ? '-checked' : '-unchecked'}`,
                        'data-checkbox': 'alert'
                      }}
                      icon={<NotificationsOffOutlinedIcon />}
                      checkedIcon={<NotificationsActiveRoundedIcon />}
                      onClick={event => handleAlertClick(event, device)}
                    />
                  </CheckboxCell>}
                <StyledTableCell
                  scope="row"
                  component="th"
                  style={{ whiteSpace: 'nowrap' }}
                  withoutHover={showModeOnly}
                >
                  <TitleCellContainer disabledBy={!!occupiedByDevice}>
                    {(isAddedDevice && !addMode && !loading) &&
                      <Tooltip title="The device has not been saved">
                        <TableAlertCheckbox as={InfoIcon} />
                      </Tooltip>}
                    <StyledChip
                      key={`${device.id} + ${device.projectId || 1}`}
                      variant="outlined"
                      label={device.label || device.title || device.uid}
                      icon={<DeviceIcon
                      /**
                       * TODO: remove freezer special-case once
                       * api/meta/device (Device API /devicetype)
                       * returns icon: 'freezer' (currently returns 'thirdparty')
                       */
                        icon={device.type === 'freezer' ?
                          'freezer' :
                          meta.device?.find(m => m.name === device.type)?.icon}
                        isError={!!occupiedByDevice}
                      />}
                      style={{ color: occupiedByDevice ? '#ba9100' : 'inherit' }}
                    />
                    <Tooltip title="Open the device">
                      <Link
                        href={`${window.location.origin}/device/${device.id}`}
                        color="inherit"
                        target="_blank"
                        onMouseDown={e => e.stopPropagation()}
                      >
                        <IconButton onClick={e => e.stopPropagation()} size="small">
                          <LaunchIcon
                            fontSize="small"
                            color="action"
                          />
                        </IconButton>
                      </Link>
                    </Tooltip>
                    {!!occupiedByDevice &&
                    <DisabledByText>
                      In project:
                        {' '}
                      { `${occupiedByDevice.disabledBy?.title}` }
                    </DisabledByText>}
                  </TitleCellContainer>
                </StyledTableCell>
                {!(isMobile && alertId) &&
                  <StyledTableCell
                    align="left"
                    withoutHover={showModeOnly}
                  >
                    {getLabel(device, meta)}
                  </StyledTableCell>}
              </StyledTableRow>
            );
          })}
        </TableBody>
      </StyledTable>
      {showChangesLabel ? <BottomInfoMessage>{saveMessage}</BottomInfoMessage> : null}
    </StyledPaper>
  );
};

export default TableView;
